import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import * as moment from 'moment';

import { environment } from '@environments/environment';
import Resources from '@constants/resourses';

import { selectedAddressModel } from 'src/app/core/models/selectedAddress.interface';

import { RestapiService } from '@services/restapi.service';
import { SessionService } from '@services/session.service';
import { UtilityService } from '@services/utility.service';
import { MatSelectModule } from '@angular/material/select';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { ArkoseComponent } from '../arkose/arkose.component';
import { ArkoseErrorDialogComponent } from '../arkose-error-dialog/arkose-error-dialog.component';


@Component({
  selector: 'app-postcode-lookup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule, ArkoseComponent],
  templateUrl: './postcode-lookup.component.html',
  styleUrl: './postcode-lookup.component.scss'
})
export class PostcodeLookupComponent implements OnInit, OnChanges, OnDestroy {
  @Input('isHomePage') isHomePage: boolean = false;
  @Input('isIndividual') isIndividual: boolean = false;
  @Input('regex') regex: any;
  @Input('isReset') isReset: boolean = false;
  @Output('isProgress') isProgress: EventEmitter<boolean> = new EventEmitter();
  @Output('addedNewAddress') addNew: EventEmitter<any> = new EventEmitter();
  @Output('resetComplete') resetComplete: EventEmitter<boolean> = new EventEmitter();
  subscriptions: Subscription[] = [];
  isLoggedIn: boolean = false;
  // id: string;
  emailId: string;
  postcode: string;
  partialPostcode: string;
  arkoseLab: string = environment.arkoseKey;

  googleAnalytics = inject(GoogleAnalyticsService);

  @ViewChild('arkose') arkose?: ArkoseComponent;

  postcodeLookupFormgroup: FormGroup;
  isAddressLookUp: boolean = false;
  isArkoseChallengeActive: boolean = false;
  addressList: any[] = [];
  gbgVersion: string = '';
  isVisible: boolean = false;

  address: selectedAddressModel;

  errorMessage: string = '';

  constructor(
    private sessionService: SessionService,
    private utilityService: UtilityService,
    private router: Router,
    private restAPI: RestapiService,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog
  ) {
    this.emailId = this.sessionService.getData(Resources.SessionStorageKeys.Email);
    this.postcode = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Postcode);
    this.partialPostcode = this.utilityService.partialPostcode(this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Postcode));
    this.isLoggedIn = this.utilityService.isLoggedIn();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reset form if isReset is true
    if (changes['isReset'] && changes['isReset'].currentValue) {
      this.address = null;
      this.postcodeLookupFormgroup.get('addressId').reset('');
      this.isVisible = false;
      this.resetComplete.emit(false);
    }
  }

  ngOnInit(): void {
    // Old password cleanup for home page
    if (this.isHomePage) {
      // Remove old postcode
      this.postcode = '';
      this.sessionService.removeItemFromLocal(Resources.SessionStorageKeys.Postcode);
    }

    // Initialise form group
    this.postcodeLookupFormgroup = this.fb.group({
      postcode: [this.isHomePage ? '' : this.postcode, [Validators.required, Validators.pattern(this.regex?.postcode || Resources.Regex.Postcode)]],
      addressId: ['']
    });

    // Dynamicly add new form control
    if (this.isHomePage) {
      this.postcodeLookupFormgroup.get('addressId').setValidators([Validators.required]);
      this.postcodeLookupFormgroup.get('addressId').updateValueAndValidity();
    }
  }

  stateSet(state: string): void {
    // check if form is valid
    if (this.postcodeLookupFormgroup.valid) {
      this.errorMessage = '';
    } else {
      this.errorMessage = 'You have entered a postcode that we don’t recognise.<br> Please try again e.g. SE12 3LA';
    }
  }

  // Triggered by the "Find address" button - runs the Arkose challenge before the actual lookup
  findAddress(): void {
    if (this.postcodeLookupFormgroup.get('postcode').valid) {
      this.isAddressLookUp = true;
      this.isArkoseChallengeActive = true;
      // Reset first - otherwise run() is a no-op once the challenge has already been completed once
      this.arkose?.reset();
      this.arkose?.run();
    } else {
      this.onError();
    }
  }

  // Method to handle postcode lookup
  // event is the Arkose completion token (when triggered via the Arkose challenge)
  postcodelookUp(event?: any): void {
    this.isArkoseChallengeActive = false;
    this.isAddressLookUp = true;
    // check if form is valid
    if (this.postcodeLookupFormgroup.get('postcode').valid) {
      const triggerAPITime = new Date().getTime();
      const postcode = this.postcodeLookupFormgroup.value.postcode?.toUpperCase()?.trim();
      this.postcode = postcode;

      this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, postcode);

      // Trigger Google Analytics event
      this.googleAnalytics.track({ partial_postcode: this.utilityService.partialPostcode(postcode) }, 'postcode_lookup');

      const isArkose = !!this.isHomePage && !!environment.arkoseKey;

      // Get address list
      this.subscriptions.push(
        this.restAPI.getAPI(`/v${isArkose ? '2' : '1'}/users/addressesWrapper?postcode=${postcode}`, event ? { arkose_token: event } : undefined).subscribe({
          next: (data: any) => {
            this.addressList = data?.data;
            this.gbgVersion = data?.gbgVersion;
            this.isAddressLookUp = false;
            this.isVisible = true;

            // Store postcode in session storage
            this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, postcode);
          }, error: (err) => {
            console.log('ERROR: ', err);

            // Trigger Google Analytics event
            this.googleAnalytics.track({ partial_postcode: this.utilityService.partialPostcode(postcode) }, 'postcode_invalid');

            this.postcodeLookupFormgroup.get('postcode').setErrors({ invalid: true });
            this.postcodeLookupFormgroup.markAllAsTouched();

            // [TEAM] Error handling - Refactoring required
            if (err?.error?.errorCode === 'CTM_INTERNAL_CHECK_ISSUE' || err?.error?.status === 'CTM_INTERNAL_CHECK_ISSUE') {
              this.errorMessage = `In the last 24 hours we've noticed a large volume of searches from your IP address so we have temporarily limited access to our site.`;
            } else if (
              err?.error?.error && err?.error?.error[0]?.message === 'postCode is not defined' ||
              err?.error?.error && err?.error?.error[0]?.message.substring(0, 36) === 'Addresses not found for the postcode' ||
              err?.error?.error === 'Invalid Postcode'
            ) {
              this.errorMessage = 'It looks like something has gone wrong while looking up your address.<br> Please check your connection and try again in a few moments.';
            } else {
              // We’re sorry, our energy service doesn’t currently support your region.
              this.errorMessage = err?.error?.msg?.description || 'You have entered a postcode that we don’t recognise.<br> Please try again e.g. SE12 3LA';
            }

            // Error email trigger
            if (err?.error?.additionalAttributes?.emailErrorRequired === 'Y') {
              const responseTimeMS = (new Date().getTime() - triggerAPITime) / 1000;
              const subject = (err?.error?.errorCode === 'CTM_INTERNAL_CHECK_ISSUE' || err?.error?.status === 'CTM_INTERNAL_CHECK_ISSUE') ? environment.ENV + ' - CTM_INTERNAL_CHECK_ISSUE - Something went wrong, please try again later.' : environment.ENV + '- PostCode Api Failed';
              this.utilityService.sendAPIError(subject, { postcode }, { postcode: err }, responseTimeMS);
            }

            this.isAddressLookUp = false;
          }
        })
      );

      // this.isAddressLookUp = false;
    } else {
      this.onError();
    }
  }

  // Error ocurred
  onError(event?: any): void {
    // When we get here from Arkose's (onError)/(onFailed) outputs, show the security-check dialog
    if (event) {
      this.dialog.open(ArkoseErrorDialogComponent, {
        width: '480px',
        maxWidth: '90vw',
        panelClass: 'arkose-error-dialog-container',
        data: {
          product: environment.arkoseProduct,
          sessionId: this.sessionService.getItemFromSession(Resources.SessionStorageKeys.CorrelationId),
          reason: event?.error?.code || event?.error || event?.type || 'unknown_error'
        }
      });
      this.isAddressLookUp = false;
      return;
    }

    this.errorMessage = 'You have entered a postcode that we don’t recognise.<br> Please try again e.g. SE12 3LA';
    this.postcodeLookupFormgroup.markAllAsTouched();
    this.isAddressLookUp = false;
  }

  // Triggered by Arkose's (onHide) output - fires both when the challenge is cancelled/closed
  // by the user and right before onCompleted, so ignore it once the challenge has completed.
  onArkoseCancelled(): void {
    if (!this.isArkoseChallengeActive) {
      return;
    }

    this.isArkoseChallengeActive = false;
    this.isAddressLookUp = false;
  }

  // Selected address
  selectedAddress(address: any): void {
    this.isAddressLookUp = true;
    // Notify to manage multiple address to show in progress
    if (this.isIndividual) {
      this.isProgress.emit(true);
    }

    // Normalise data - In the landing page return only addressId, based on id get back the address.
    if (this.isHomePage) {
      address = this.addressList.filter((item) => item.addressId === address.addressId)[0];
    }

    // flat address mpan, take first element from array if it is present
    if (address?.mpan) {
      address['mpan'] = address?.mpan?.[0];
    }
    // flat address mprn, take first element from array if it is present
    if (address?.mprn) {
      address['mprn'] = address?.mprn?.[0];
    }

    // Trigger Google Analytics event
    this.googleAnalytics.track({
      partial_postcode: this.utilityService.partialPostcode(address?.postcode),
      address: address?.addressId
    }, 'address_selected');

    // If MPAN is not present in address, then get meter number
    if (!address?.mpan) {
      this.getMeter(address);
    } else {
      this.checkBlockedAddress(address);
    }
  }

  // Get meter number
  getMeter(address: any): void {
    const payload = {
      addressId: address?.addressId,
      gbgVersion: this.gbgVersion
    };

    this.subscriptions.push(
      this.restAPI.postAPI('/v1/users/meterWrapper', payload).subscribe({
        next: (data: any) => {
          // MPAN
          if (data?.mpan) {
            address['mpan'] = data?.mpan;
          }
          // MPRN
          if (data?.mprn) {
            address['mprn'] = data?.mprn;
          }

          // Not executed if it is coming from home page
          if (!this.isHomePage) {
            // Return new address details or navigate to dashboard
            this.addNewAddress(address);
          } else {
            // If it is coming from home page, then check blocked address
            this.checkBlockedAddress(address);
          }
        }, error: (err) => {
          console.log('ERROR: ', err);
        }
      })
    );
  }

  // Check blocked address
  checkBlockedAddress(address: any): void {
    const cPath = '/v2/users/blocked-address-check?'
    const queryParams = {
      label: address?.Text,
      addressId: address?.addressId,
      fuelType: address?.fuelType,
      postcode: address?.postcode
    }
    let payload = {};
    // Add MPAN to payload
    if (address?.mpan) {
      payload['mpan'] = address?.mpan;
    }
    // Add MPRN to payload
    if (address?.mprn) {
      payload['mprn'] = address?.mprn;
    }

    this.subscriptions.push(
      this.restAPI.postAPI(cPath + new URLSearchParams(queryParams), payload).subscribe({
        next: (data: any) => {
          this.sessionService.setData(Resources.SessionStorageKeys.IsAddressBlocked, data?.addressBlocked);
          this.sessionService.removeData(Resources.SessionStorageKeys.CurrentUsage);
          this.sessionService.removeData(Resources.SessionStorageKeys.NetzeroAPI);

          // Return new address details or navigate to dashboard
          this.addNewAddress(address);

        }, error: (err) => {
          console.log('ERROR: ', err);
          this.sessionService.setData(Resources.SessionStorageKeys.IsAddressBlocked, false);
          this.sessionService.removeData(Resources.SessionStorageKeys.CurrentUsage);
          this.sessionService.removeData(Resources.SessionStorageKeys.NetzeroAPI);

          // Return new address details or navigate to dashboard
          this.addNewAddress(address);
        }
      })
    );
  }

  // Navigate to dashboard
  addNewAddress(address: any): void {
    const selectedAddress: selectedAddressModel = {
      addressId: address?.addressId,
      addressText: address?.Text,
      addressPostcode: address?.postcode,
      gbgVersion: this.gbgVersion,
      dnoId: address?.dnoId
    };

    // MPAN
    if (address?.mpan) {
      selectedAddress['mpan'] = address?.mpan;
    }

    // MPRN
    if (address?.mprn) {
      selectedAddress['mprn'] = address?.mprn;
    }

    // Store only if not coming from home page
    if (!this.isHomePage) {
      // Store selected address in session storage
      this.sessionService.setData(Resources.SessionStorageKeys.SelectedAddress, selectedAddress);
      this.sessionService.setData(Resources.SessionStorageKeys.DnoId, selectedAddress?.dnoId);
    }

    if (!this.isIndividual) {
      // Navigate to dashboard
      const queryParams = this.activatedRoute.snapshot.queryParams;
      this.router.navigate(['/information'], { queryParams: queryParams });
    } else {
      // Created address response
      const addressResponse = {
        addressId: selectedAddress?.addressId,
        addressLine: selectedAddress?.addressText,
        gbgVersion: selectedAddress?.gbgVersion,
        internalAddressId: selectedAddress?.addressId,
        dnoId: selectedAddress?.dnoId,
        postcode: selectedAddress?.addressPostcode,
        switchFlag: false,
        updatedAt: moment().format('YYYY-MM-DD HH:mm:ss')
      };
      // Notify to manage multiple address
      this.addNew.emit({ addNewAddress: addressResponse, selectedAddress: selectedAddress });
      this.address = selectedAddress;
    }

    // Removed EV details from session storage
    this.sessionService.removeData(Resources.SessionStorageKeys.EVDETAILS);
    this.isAddressLookUp = false;
  }

  // Hide dropdown
  hide(): boolean {
    this.isVisible = false;
    return true;
  }

  ngOnDestroy(): void {
    // Cleanup if necessary
    this.subscriptions.forEach((subscription) => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }
}
